import { Icon } from '@toggle/design-system';
import { useTranslation } from 'next-i18next';
import React, { FC, Fragment, memo, ReactNode } from 'react';

import { CompanyWithReportingLabel } from '@/api/search/search-schema';

import { formatAssetName } from '../../utils/company-utils/companyUtils';
import {
  formatSurprisePercentage,
  SurprisePercentageFormat,
} from '../../utils/search-utils/searchUtils';
import * as S from './SearchItem.styles';

const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const highlight = (text: string, highlighted: string): ReactNode => {
  const parts = text.split(new RegExp(`(${escapeRegExp(highlighted)})`, 'gi'));
  return parts.map((part, i) => (
    <Fragment key={i}>
      {part.toUpperCase() === highlighted.toUpperCase() ? (
        <mark data-testid="highlighted">{part}</mark>
      ) : (
        part
      )}
    </Fragment>
  ));
};

export interface SearchItemProps {
  data: CompanyWithReportingLabel;
  searchValue: string;
}

export const SearchItem: FC<SearchItemProps> = memo(({ data, searchValue }) => {
  const { t } = useTranslation('common');
  const isReported = new Date(data.reporting_date) < new Date();

  const formattedSurprisePercentage: SurprisePercentageFormat = isReported
    ? formatSurprisePercentage(data, t)
    : { label: data.reporting_label };

  return (
    <S.ContainerLink data-testid="company" href={formatAssetName(data)}>
      <S.InfoContainer>
        <S.Name>
          {searchValue
            ? highlight(data.company_name, searchValue)
            : data.company_name}
        </S.Name>
        <S.Ticker>
          {searchValue ? highlight(data.ticker, searchValue) : data.ticker}
        </S.Ticker>
      </S.InfoContainer>
      <S.DateContainer>
        {!isReported && (
          <Icon iconName="Calendar" fillColor="var(--icon-soft)" size={14} />
        )}
        <S.ReportedContainer>
          <S.ReportedPercent status={formattedSurprisePercentage.status}>
            {formattedSurprisePercentage.surprise}
          </S.ReportedPercent>
          <S.ReportedLabel
            data-testid="reported-date"
            status={formattedSurprisePercentage.status}
          >
            {formattedSurprisePercentage.label}
          </S.ReportedLabel>
        </S.ReportedContainer>
      </S.DateContainer>
    </S.ContainerLink>
  );
});
